import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Pencil, Plus, Save, Search, ShieldCheck, X } from 'lucide-react'
import type { PermitListItem } from 'modules/permit/api/permitService'
import { DatePicker } from 'modules/permit/components/DatePicker'
import { PageTitle } from 'shared/components/layout/PageTitle'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Button } from 'shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Checkbox } from 'shared/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { RadioGroup, RadioGroupItem } from 'shared/ui/radio-group'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/ui/table'
import { Textarea } from 'shared/ui/textarea'
import { getPersistedSelectedSiteId, resolveTenantCandidate, resolveTenantId } from 'shared/utils/tenantContext'
import {
  createPermitAudit,
  deletePermitAuditList,
  getMasterNonConform,
  getPermitAuditById,
  searchAuditPermits,
  updatePermitAudit,
  updatePermitAuditReview,
  validatePermitAudit,
  type NonConformItem,
  type PermitAuditAttachmentUpload,
  type PermitAuditDetailItem,
  type PermitAuditRecord,
} from '../api/permitAuditService'
import {
  collectAuditPlantTokens,
  collectAuditRoleTokens,
  collectAuditUserIdentityTokens,
  isAuditAreaOwnerRole,
  isAuditSafetyRole,
  matchesAuditIdentity,
  matchesAuditPlant,
  resolveAuditAccessibleLocationIds,
} from '../utils/permissions'

type LayoutState = {
  user?: any
  module?: 'E-PTW' | 'ADMIN'
  site?: {
    id?: string
    name?: string
  }
  menus?: any[]
  roles?: any[]
  tenantName?: string
}

type PermitSelectionItem = {
  permitId?: string | null
  permitNo: string
  title?: string | null
  workDescr?: string | null
  plant?: string | null
  unit?: string | null
  location?: string | null
  company?: string | null
  permitType?: string | null
  plannedStart?: string | null
  plannedEnd?: string | null
  statusName?: string | null
}

type AttachmentFormItem = PermitAuditAttachmentUpload & {
  id: string
}

type AuditFormState = {
  auditId?: number
  permitAuditList: string[]
  auditResult: 'Conform' | 'Nonconform' | ''
  auditRemark: string
  nonConformResult: '' | 'Y' | 'N'
  dueDate: string
  nonConformRemark: string
  actionRemark: string
  reviewResult: '' | 'Y' | 'N'
  reviewRemark: string
}

const createInitialForm = (): AuditFormState => ({
  permitAuditList: [],
  auditResult: '',
  auditRemark: '',
  nonConformResult: '',
  dueDate: '',
  nonConformRemark: '',
  actionRemark: '',
  reviewResult: '',
  reviewRemark: '',
})

const createAttachmentId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatPermitDatePart = (value?: string | null) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const day = String(parsed.getDate()).padStart(2, '0')
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const year = String(parsed.getFullYear())
  return `${day}-${month}-${year}`
}

const formatPermitTimePart = (value?: string | null) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const hours = String(parsed.getHours()).padStart(2, '0')
  const minutes = String(parsed.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

const formatPermitDateRange = (start?: string | null, end?: string | null) => {
  const startText = formatPermitDatePart(start)
  const endText = formatPermitDatePart(end)
  if (startText && endText) {
    return startText === endText ? startText : `${startText} - ${endText}`
  }
  return startText ?? endText ?? '-'
}

const formatPermitTimeRange = (start?: string | null, end?: string | null) => {
  const startText = formatPermitTimePart(start)
  const endText = formatPermitTimePart(end)
  if (startText && endText) {
    return `${startText} - ${endText}`
  }
  return startText ?? endText ?? '-'
}

const normalizeDateValue = (value?: string | null) => {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim()
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toISOString().slice(0, 10)
}

const normalizeText = (value?: string | null) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

const parsePermitLocation = (location?: string | null) => {
  const normalized = normalizeText(location)
  if (!normalized) {
    return { plant: null, unit: null }
  }

  const separator = normalized.includes('/') ? '/' : normalized.includes(':') ? ':' : null
  if (!separator) {
    return { plant: normalized, unit: null }
  }

  const [plant, unit] = normalized
    .split(separator)
    .map((item) => normalizeText(item))

  return {
    plant: plant ?? null,
    unit: unit ?? null,
  }
}

const mapRecordToForm = (record: PermitAuditRecord): AuditFormState => ({
  auditId: record.auditId,
  permitAuditList: record.permitNo ? [record.permitNo] : [],
  auditResult: (record.auditResult as AuditFormState['auditResult']) ?? '',
  auditRemark: record.auditRemark ?? '',
  nonConformResult: (record.nonConformResult as AuditFormState['nonConformResult']) ?? '',
  dueDate: normalizeDateValue(record.dueDate),
  nonConformRemark: record.nonConformRemark ?? '',
  actionRemark: record.actionRemark ?? '',
  reviewResult: (record.reviewResult as AuditFormState['reviewResult']) ?? '',
  reviewRemark: record.reviewRemark ?? '',
})

const mapDetailItems = (masterItems: NonConformItem[], detailItems: PermitAuditDetailItem[]) =>
  masterItems.map((item) => {
    const matched = detailItems.find((detail) => detail.nonconformId === item.nonConFormId)
    return {
      nonconformId: item.nonConFormId,
      checked: matched?.checked ?? 'N',
      remark: matched?.remark ?? '',
      type: item.type,
      descr: item.descr,
    }
  })

const mapAttachments = (record: PermitAuditRecord): AttachmentFormItem[] =>
  (record.attach ?? []).map((item) => ({
    id: createAttachmentId(),
    docType: item.docType,
    docName: item.docName,
    docExtension: item.docExtension,
    fileUrl: item.fileUrl ?? null,
    fileType: item.fileType ?? null,
    file: null,
  }))

const getPermitPlant = (permit?: PermitSelectionItem | null) =>
  normalizeText(permit?.plant ?? parsePermitLocation(permit?.location).plant)
const getPermitUnit = (permit?: PermitSelectionItem | null) =>
  normalizeText(permit?.unit ?? parsePermitLocation(permit?.location).unit)

export function PermitAuditEditorPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams<{ auditId: string }>()
  const state = useMemo(() => ((location.state as LayoutState | undefined) ?? {}), [location.state])
  const persistedSiteId = useMemo(() => getPersistedSelectedSiteId(), [])
  const tenantId = resolveTenantId(state.site?.id, persistedSiteId)
  const siteId = resolveTenantCandidate(state.site?.id, persistedSiteId)
  const routeAuditId = useMemo(() => Number(params.auditId ?? 0), [params.auditId])
  const isViewMode = location.pathname.endsWith('/view')
  const isCreateMode = location.pathname.endsWith('/create')

  const [form, setForm] = useState<AuditFormState>(createInitialForm)
  const [detailItems, setDetailItems] = useState<PermitAuditDetailItem[]>([])
  const [attachments, setAttachments] = useState<AttachmentFormItem[]>([])
  const [selectedPermits, setSelectedPermits] = useState<PermitSelectionItem[]>([])
  const [record, setRecord] = useState<PermitAuditRecord | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSavingReview, setIsSavingReview] = useState(false)
  const [isPermitDialogOpen, setIsPermitDialogOpen] = useState(false)
  const [permitSearch, setPermitSearch] = useState('')
  const [permitSearchTrigger, setPermitSearchTrigger] = useState(0)
  const [permitDateFrom, setPermitDateFrom] = useState('')
  const [permitDateTo, setPermitDateTo] = useState('')
  const [permitSearchResults, setPermitSearchResults] = useState<PermitSelectionItem[]>([])
  const [isPermitSearchLoading, setIsPermitSearchLoading] = useState(false)
  const [selectedPermitMap, setSelectedPermitMap] = useState<Record<string, PermitSelectionItem>>({})

  const roleTokens = useMemo(() => collectAuditRoleTokens(state), [state])
  const userIdentityTokens = useMemo(() => collectAuditUserIdentityTokens(state), [state])
  const plantTokens = useMemo(() => collectAuditPlantTokens(state), [state])
  const canReview = useMemo(() => isAuditSafetyRole(roleTokens), [roleTokens])
  const isAreaOwnerRole = useMemo(() => isAuditAreaOwnerRole(roleTokens), [roleTokens])
  const accessibleLocationIds = useMemo(
    () => resolveAuditAccessibleLocationIds(state.user, state.roles),
    [state.user, state.roles],
  )
  const samePlantSelection = useMemo(() => {
    if (selectedPermits.length <= 1) return true
    const plant = getPermitPlant(selectedPermits[0])
    return selectedPermits.every((item) => getPermitPlant(item) === plant)
  }, [selectedPermits])
  const currentPlant = useMemo(
    () => record?.org ?? record?.plant ?? getPermitPlant(selectedPermits[0]) ?? null,
    [record?.org, record?.plant, selectedPermits],
  )
  const isOwnerDoc = useMemo(() => {
    if (isCreateMode) return true
    return matchesAuditIdentity(record?.auditBy, userIdentityTokens)
  }, [isCreateMode, record?.auditBy, userIdentityTokens])
  const hasAreaOwnerLocationAccess = useMemo(() => {
    const normalized = typeof currentPlant === 'string' ? currentPlant.trim().toLowerCase() : null
    return normalized ? accessibleLocationIds.has(normalized) : false
  }, [currentPlant, accessibleLocationIds])
  const isPlantOwner = useMemo(
    () => matchesAuditPlant(currentPlant, plantTokens) || (isAreaOwnerRole && hasAreaOwnerLocationAccess),
    [currentPlant, plantTokens, isAreaOwnerRole, hasAreaOwnerLocationAccess],
  )
  const canEditAuditSection = useMemo(() => isCreateMode || isOwnerDoc, [isCreateMode, isOwnerDoc])
  const canEditActionSection = useMemo(() => isCreateMode || isPlantOwner, [isCreateMode, isPlantOwner])
  const canManageAuditAttachments = useMemo(() => isCreateMode || isOwnerDoc, [isCreateMode, isOwnerDoc])
  const canManageActionAttachments = useMemo(() => isCreateMode || isPlantOwner, [isCreateMode, isPlantOwner])
  const canSaveAudit = useMemo(() => isCreateMode || isOwnerDoc || isPlantOwner, [isCreateMode, isOwnerDoc, isPlantOwner])

  useEffect(() => {
    if (!state.user) {
      navigate('/modules', { replace: true })
    }
  }, [navigate, state.user])

  useEffect(() => {
    let isCancelled = false

    const bootstrap = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const master = await getMasterNonConform()
        if (isCancelled) return

        if (isCreateMode || !routeAuditId) {
          setDetailItems(
            master.map((item) => ({
              nonconformId: item.nonConFormId,
              checked: 'N',
              remark: '',
              type: item.type,
              descr: item.descr,
            })),
          )
          return
        }

        const loaded = await getPermitAuditById(routeAuditId)
        if (isCancelled) return

        if (!loaded) {
          setError('Permit audit record was not found.')
          return
        }

        setRecord(loaded)
        setForm(mapRecordToForm(loaded))
        setDetailItems(mapDetailItems(master, loaded.detail ?? []))
        setAttachments(mapAttachments(loaded))
        setSelectedPermits([
          {
            permitNo: loaded.permitNo,
            workDescr: loaded.workDescr ?? null,
            plant: loaded.plant ?? null,
            unit: loaded.unit ?? null,
            location: loaded.plant && loaded.unit ? `${loaded.plant}/${loaded.unit}` : loaded.plant ?? null,
          },
        ])
      } catch (err: any) {
        if (isCancelled) return
        setError(err?.message ?? 'Unable to load permit audit data.')
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    void bootstrap()

    return () => {
      isCancelled = true
    }
  }, [isCreateMode, routeAuditId])

  useEffect(() => {
    if (!isPermitDialogOpen || !tenantId) return

    let isCancelled = false

      const loadPermits = async () => {
      setIsPermitSearchLoading(true)
      try {
        const response = await searchAuditPermits({
          tenantId,
          siteId,
          searchTerm: permitSearch.trim() || undefined,
          plannedStartFrom: permitDateFrom || undefined,
          plannedEndTo: permitDateTo || undefined,
          pageNumber: 1,
          pageSize: 10,
        })
        if (isCancelled) return
        setPermitSearchResults(
          (response.items ?? []).map((item: PermitListItem) => {
            const locationText = item.locationName ?? item.location ?? null
            const parsedLocation = parsePermitLocation(locationText)

            return {
              permitId: item.permitId,
              permitNo: item.permitNumber,
              title: item.title ?? null,
              workDescr: item.workDescription || item.title,
              plant: item.area ?? parsedLocation.plant,
              unit: item.unit ?? item.unitName ?? parsedLocation.unit,
              location: locationText,
              company: item.requesterCompany ?? null,
              permitType: item.permitType ?? null,
              plannedStart: item.plannedStart ?? null,
              plannedEnd: item.plannedEnd ?? null,
              statusName: item.statusName ?? item.status,
            }
          }),
        )
      } catch (err: any) {
        if (isCancelled) return
        setError(err?.message ?? 'Unable to search permits for audit creation.')
      } finally {
        if (!isCancelled) {
          setIsPermitSearchLoading(false)
        }
      }
    }

    void loadPermits()

    return () => {
      isCancelled = true
    }
    // Only re-run when the dialog opens or the user explicitly clicks Search -
    // permitSearch/permitDateFrom/permitDateTo are read at call time, not watched,
    // so typing/picking a date no longer fires a request on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermitDialogOpen, permitSearchTrigger, siteId, tenantId])

  const selectedAuditAttachments = useMemo(
    () => attachments.filter((item) => item.docType === 'Audit'),
    [attachments],
  )
  const selectedActionAttachments = useMemo(
    () => attachments.filter((item) => item.docType === 'Action'),
    [attachments],
  )

  const showNonConformSection = form.auditResult === 'Nonconform'
  const showActionSections = showNonConformSection && form.nonConformResult === 'N'

  const handleUpdateForm = <K extends keyof AuditFormState>(key: K, value: AuditFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleToggleDetail = (nonconformId: number, checked: boolean) => {
    setDetailItems((prev) =>
      prev.map((item) =>
        item.nonconformId === nonconformId
          ? { ...item, checked: checked ? 'Y' : 'N', remark: checked ? item.remark : '' }
          : item,
      ),
    )
  }

  const handleDetailRemark = (nonconformId: number, remark: string) => {
    setDetailItems((prev) =>
      prev.map((item) => (item.nonconformId === nonconformId ? { ...item, remark } : item)),
    )
  }

  const handleFileChange = (docType: 'Audit' | 'Action', fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return

    const existingCount = attachments.filter((item) => item.docType === docType).length
    if (existingCount >= 5) {
      setError(`Maximum 5 ${docType.toLowerCase()} attachments are allowed.`)
      return
    }

    const file = fileList[0]
    const segments = file.name.split('.')
    const extension = segments.length > 1 ? segments.pop() ?? '' : ''
    const docName = segments.join('.') || file.name

    const nextItem: AttachmentFormItem = {
      id: createAttachmentId(),
      docType,
      docName,
      docExtension: extension,
      file,
      fileUrl: URL.createObjectURL(file),
      fileType: file.type,
    }

    setAttachments((prev) => [...prev, nextItem])
    setError(null)
  }

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((item) => item.id !== id))
  }

  const handleOpenPermitDialog = () => {
    const currentMap = selectedPermits.reduce<Record<string, PermitSelectionItem>>((acc, item) => {
      acc[item.permitNo] = item
      return acc
    }, {})
    setSelectedPermitMap(currentMap)
    setPermitSearch('')
    setPermitDateFrom('')
    setPermitDateTo('')
    setIsPermitDialogOpen(true)
  }

  const handleApplySelectedPermits = () => {
    const nextPermits = Object.values(selectedPermitMap)
    setSelectedPermits(nextPermits)
    handleUpdateForm(
      'permitAuditList',
      nextPermits.map((item) => item.permitNo),
    )
    setIsPermitDialogOpen(false)
  }

  const validateForm = () => {
    if (form.permitAuditList.length === 0) {
      return 'Please select at least one permit.'
    }
    if (!form.auditResult) {
      return 'Please select audit result.'
    }
    if (!form.auditRemark.trim()) {
      return 'Please provide audit summary.'
    }
    if (form.auditResult === 'Nonconform') {
      if (!samePlantSelection) {
        return 'Nonconform audit requires permits from the same plant.'
      }
      const selectedItems = detailItems.filter((item) => item.type === 'SUB' && item.checked === 'Y')
      if (selectedItems.length === 0) {
        return 'Please select at least one non-conform topic.'
      }
      const hasOtherWithoutRemark = selectedItems.some(
        (item) => item.descr.toLowerCase() === 'other' && !item.remark?.trim(),
      )
      if (hasOtherWithoutRemark) {
        return 'Please provide remark for Other non-conform topic.'
      }
      if (!form.nonConformResult) {
        return 'Please select corrective action status.'
      }
    }
    return null
  }

  const buildSavePayload = () => ({
    auditId: form.auditId ?? null,
    permitAuditList: form.permitAuditList,
    auditResult: form.auditResult,
    auditRemark: form.auditRemark.trim(),
    nonConformResult: form.nonConformResult || null,
    dueDate: normalizeText(form.dueDate),
    nonConformRemark: normalizeText(form.nonConformRemark),
    actionRemark: normalizeText(form.actionRemark),
    reviewResult: form.reviewResult || null,
    reviewRemark: normalizeText(form.reviewRemark),
    detail: detailItems.filter((item) => item.type === 'SUB' && item.checked === 'Y'),
    attachments: attachments.map((item) => ({
      docType: item.docType,
      docName: item.docName,
      docExtension: item.docExtension,
      file: item.file ?? null,
      fileUrl: item.fileUrl ?? null,
      fileType: item.fileType ?? null,
    })),
  })

  const handleSave = async () => {
    if (!canSaveAudit) {
      setError('You do not have permission to update this permit audit.')
      return
    }

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      if (isCreateMode) {
        const hasDuplicate = await validatePermitAudit({ permitAuditList: form.permitAuditList })
        if (hasDuplicate) {
          const confirmed = window.confirm(
            'Existing audit records were found for the selected permit(s). Replace them with the new submission?',
          )
          if (!confirmed) {
            return
          }
          await deletePermitAuditList({ permitAuditList: form.permitAuditList })
        }

        await createPermitAudit(buildSavePayload())
      } else {
        await updatePermitAudit(buildSavePayload())
      }

      navigate('/permit-audit', { replace: true, state })
    } catch (err: any) {
      setError(err?.message ?? 'Unable to save permit audit.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveReview = async () => {
    if (!form.auditId) {
      setError('Review can only be saved after the audit record exists.')
      return
    }
    if (!form.reviewResult) {
      setError('Please select review result.')
      return
    }

    setIsSavingReview(true)
    setError(null)
    try {
      await updatePermitAuditReview({
        auditId: form.auditId,
        reviewResult: form.reviewResult,
        reviewRemark: normalizeText(form.reviewRemark),
      })
      const refreshed = await getPermitAuditById(form.auditId)
      if (refreshed) {
        setRecord(refreshed)
        setForm(mapRecordToForm(refreshed))
      }
    } catch (err: any) {
      setError(err?.message ?? 'Unable to save permit audit review.')
    } finally {
      setIsSavingReview(false)
    }
  }

  const groupedNonConformItems = useMemo(() => {
    const groups: Array<{ header: string; items: PermitAuditDetailItem[] }> = []
    let currentHeader = 'Non-conform Topics'

    detailItems.forEach((item) => {
      if (item.type === 'HEADER') {
        currentHeader = item.descr
        groups.push({ header: currentHeader, items: [] })
        return
      }
      if (item.type === 'SUB') {
        if (groups.length === 0) {
          groups.push({ header: currentHeader, items: [] })
        }
        groups[groups.length - 1].items.push(item)
      }
    })

    return groups
  }, [detailItems])

  const renderAttachmentList = (items: AttachmentFormItem[], canManageAttachments: boolean) => {
    if (items.length === 0) {
      return <p className="text-sm text-gray-500">No attachments</p>
    }

    return (
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={item.id} className="rounded-lg border border-dashed border-slate-300 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <span className="font-medium">{index + 1}. </span>
                {item.docName}.{item.docExtension}
              </div>
              <div className="flex items-center gap-2">
                {item.fileUrl ? (
                  <a
                    href={item.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Open
                  </a>
                ) : null}
                {!isViewMode && canManageAttachments ? (
                  <Button variant="outline" size="sm" onClick={() => handleRemoveAttachment(item.id)}>
                    <X className="mr-1 h-4 w-4" />
                    Remove
                  </Button>
                ) : null}
              </div>
            </div>
            {item.fileType?.startsWith('image/') && item.fileUrl ? (
              <img src={item.fileUrl} alt={item.docName} className="mt-3 max-h-64 rounded-md border object-contain" />
            ) : null}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <Button variant="outline" onClick={() => navigate('/permit-audit', { state })}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to List
          </Button>
        </div>
        <div className="flex-1">
          <PageTitle
            currentPage="permit-audit"
            compact
            titleOverride={isViewMode ? 'View Permit Audit' : isCreateMode ? 'Create Permit Audit' : 'Edit Permit Audit'}
            descriptionOverride=""
          />
        </div>
      </div>

      {error && !isCreateMode ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 text-sm text-gray-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading permit audit data...
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Permit Reference</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                <div className="flex-1">
                  <Label>Selected Permit No</Label>
                  <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 text-sm">
                    {form.permitAuditList.length > 0 ? form.permitAuditList.join(', ') : 'No permits selected'}
                  </div>
                </div>
                {isCreateMode && !isViewMode ? (
                  <Button onClick={handleOpenPermitDialog}>
                    <Search className="mr-2 h-4 w-4" />
                    Select Permit
                  </Button>
                ) : null}
              </div>

              {selectedPermits.length > 0 ? (
                <div className="rounded-lg border border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Permit No</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Plant</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPermits.map((permit) => (
                        <TableRow key={permit.permitNo}>
                          <TableCell className="font-medium">{permit.permitNo}</TableCell>
                          <TableCell>{permit.workDescr ?? '-'}</TableCell>
                          <TableCell>{getPermitPlant(permit) ?? '-'}</TableCell>
                          <TableCell>{getPermitUnit(permit) ?? '-'}</TableCell>
                          <TableCell>{permit.statusName ?? '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>สรุปผลการตรวจสอบความปลอดภัยเบื้องต้น</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Conform or Nonconform</Label>
                <RadioGroup
                  value={form.auditResult}
                  onValueChange={(value) => {
                    const nextValue = value as AuditFormState['auditResult']
                    if (nextValue === 'Nonconform' && !samePlantSelection) {
                      setError('Selected permits must belong to the same plant for nonconform audit.')
                      handleUpdateForm('auditResult', '')
                      return
                    }

                    setError(null)
                    handleUpdateForm('auditResult', nextValue)
                    if (nextValue !== 'Nonconform') {
                      handleUpdateForm('nonConformResult', '')
                      handleUpdateForm('dueDate', '')
                      handleUpdateForm('nonConformRemark', '')
                      handleUpdateForm('actionRemark', '')
                      handleUpdateForm('reviewResult', '')
                      handleUpdateForm('reviewRemark', '')
                    }
                  }}
                  disabled={isViewMode || !canEditAuditSection}
                  className="flex flex-wrap gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Conform" id="audit-conform" />
                    <Label htmlFor="audit-conform">Conform</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Nonconform" id="audit-nonconform" />
                    <Label htmlFor="audit-nonconform">Nonconform</Label>
                  </div>
                </RadioGroup>
                {showNonConformSection && !samePlantSelection ? (
                  <p className="text-sm text-amber-600">Selected permits must belong to the same plant for nonconform audit.</p>
                ) : null}
              </div>

              {showNonConformSection ? (
                <div className="space-y-4 rounded-xl bg-amber-50 p-4">
                  {groupedNonConformItems.length === 0 ? (
                    <p className="text-sm text-slate-600">Non-conform master data has not been configured yet.</p>
                  ) : null}
                  {groupedNonConformItems.map((group) => (
                    <div key={group.header} className="space-y-3">
                      <h4 className="text-sm font-semibold text-slate-800">{group.header}</h4>
                      <div className="space-y-2">
                        {group.items.map((item) => (
                          <div key={item.nonconformId} className="flex flex-col gap-2 rounded-md bg-white p-3">
                            {item.descr.toLowerCase() === 'other' ? (
                              <div className="flex flex-wrap items-center gap-3">
                                <Checkbox
                                  checked={item.checked === 'Y'}
                                  onCheckedChange={(checked) => handleToggleDetail(item.nonconformId, checked === true)}
                                  disabled={isViewMode || !canEditAuditSection}
                                />
                                <p className="text-sm">Other</p>
                                <Input
                                  value={item.remark ?? ''}
                                  onChange={(event) => handleDetailRemark(item.nonconformId, event.target.value)}
                                  disabled={item.checked !== 'Y' || isViewMode || !canEditAuditSection}
                                  placeholder="Please describe"
                                  className="w-full min-w-[220px] sm:w-80"
                                />
                              </div>
                            ) : (
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  checked={item.checked === 'Y'}
                                  onCheckedChange={(checked) => handleToggleDetail(item.nonconformId, checked === true)}
                                  disabled={isViewMode || !canEditAuditSection}
                                />
                                <p className="text-sm">{item.descr}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="auditRemark">สรุปผลการตรวจสอบความปลอดภัยเบื้องต้น</Label>
                <Textarea
                  id="auditRemark"
                  rows={4}
                  value={form.auditRemark}
                  onChange={(event) => handleUpdateForm('auditRemark', event.target.value)}
                  readOnly={isViewMode || !canEditAuditSection}
                  placeholder='ตัวอย่างเช่น "ผู้รับเหมาไม่ตรวจวัดแก๊สตามระยะเวลาที่กำหนด", "ผู้รับเหมา 2 ราย ไม่สวมแว่นตานิรภัยขณะปฏิบัติงาน"'
                />
              </div>

              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label>รูปภาพ / เอกสารประกอบ</Label>
                  {!isViewMode && canManageAuditAttachments ? (
                    <div>
                      <input
                        id="audit-file-input"
                        type="file"
                        className="hidden"
                        onChange={(event) => {
                          handleFileChange('Audit', event.target.files)
                          event.currentTarget.value = ''
                        }}
                      />
                      <Button variant="outline" onClick={() => document.getElementById('audit-file-input')?.click()}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add File
                      </Button>
                    </div>
                  ) : null}
                </div>
                {renderAttachmentList(selectedAuditAttachments, canManageAuditAttachments)}
              </div>
            </CardContent>
          </Card>

          {showNonConformSection ? (
            <Card>
              <CardHeader>
                <CardTitle>การดำเนินการแก้ไขประเด็น Non Conform</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <RadioGroup
                    value={form.nonConformResult}
                    onValueChange={(value) => handleUpdateForm('nonConformResult', value as AuditFormState['nonConformResult'])}
                    disabled={isViewMode || !canEditAuditSection}
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Y" id="nonconform-y" />
                      <Label htmlFor="nonconform-y">สามารถดำเนินการแก้ไขจากหน้างานได้</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="N" id="nonconform-n" />
                      <Label htmlFor="nonconform-n">ไม่สามารถดำเนินการแก้ไขจากหน้างานได้</Label>
                    </div>
                  </RadioGroup>
                </div>

                {showActionSections ? (
                  <>
                    <div>
                      <Label htmlFor="dueDate">ระบุวันที่คาดว่าจะดำเนินการแก้ไขแล้วเสร็จ (เจ้าของพื้นที่)</Label>
                      <DatePicker
                        id="dueDate"
                        value={form.dueDate}
                        onChange={(value) => handleUpdateForm('dueDate', value)}
                        disabled={isViewMode || !canEditActionSection}
                        className="mt-1 h-10"
                      />
                    </div>
                    <div>
                      <Label htmlFor="nonConformRemark">ระบุรายละเอียดการดำเนินการแก้ไข (เจ้าของพื้นที่)</Label>
                      <Input
                        id="nonConformRemark"
                        value={form.nonConformRemark}
                        onChange={(event) => handleUpdateForm('nonConformRemark', event.target.value)}
                        readOnly={isViewMode || !canEditActionSection}
                        placeholder="Describe the corrective action plan"
                        className="mt-1"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="actionRemark">ผลการดำเนินการแก้ไขประเด็น Non Conform (เจ้าของพื้นที่)</Label>
                      <Textarea
                        id="actionRemark"
                        rows={3}
                        value={form.actionRemark}
                        onChange={(event) => handleUpdateForm('actionRemark', event.target.value)}
                        readOnly={isViewMode || !canEditActionSection}
                        placeholder="Describe what was completed at site"
                      />
                    </div>

                    {record?.actionByName || record?.actionAddDate ? (
                      <p className="text-sm text-rose-600">
                        [{record?.actionByName ?? record?.actionAddBy ?? '-'} : {formatDateTime(record?.actionAddDate)}]
                      </p>
                    ) : null}

                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <Label>รูปภาพ / เอกสารประกอบ</Label>
                        {!isViewMode && canManageActionAttachments ? (
                          <div>
                            <input
                              id="action-file-input"
                              type="file"
                              className="hidden"
                              onChange={(event) => {
                                handleFileChange('Action', event.target.files)
                                event.currentTarget.value = ''
                              }}
                            />
                            <Button variant="outline" onClick={() => document.getElementById('action-file-input')?.click()}>
                              <Plus className="mr-2 h-4 w-4" />
                              Add File
                            </Button>
                          </div>
                        ) : null}
                      </div>
                      {renderAttachmentList(selectedActionAttachments, canManageActionAttachments)}
                    </div>
                  </>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {showActionSections ? (
            <Card>
              <CardHeader>
                <CardTitle>ผลการตรวจสอบการดำเนินการแก้ไขประเด็น Non Conform (Safety)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <RadioGroup
                    value={form.reviewResult}
                    onValueChange={(value) => handleUpdateForm('reviewResult', value as AuditFormState['reviewResult'])}
                    disabled={isViewMode || !canReview}
                    className="flex flex-wrap gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Y" id="review-pass" />
                      <Label htmlFor="review-pass">ผ่าน</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="N" id="review-fail" />
                      <Label htmlFor="review-fail">ไม่ผ่าน</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reviewRemark">หมายเหตุ</Label>
                  <Textarea
                    id="reviewRemark"
                    rows={3}
                    value={form.reviewRemark}
                    onChange={(event) => handleUpdateForm('reviewRemark', event.target.value)}
                    readOnly={isViewMode || !canReview}
                    placeholder="Provide review comments"
                  />
                </div>

                {record?.reviewByName || record?.reviewDate ? (
                  <p className="text-sm text-rose-600">
                    [{record?.reviewByName ?? record?.reviewBy ?? '-'} : {formatDateTime(record?.reviewDate)}]
                  </p>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {!isViewMode && !isCreateMode && (canSaveAudit || (showActionSections && canReview)) ? (
            <div className="flex flex-wrap justify-end gap-3">
              {canSaveAudit ? (
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save Audit
                </Button>
              ) : null}
              {showActionSections && canReview ? (
                <Button variant="outline" onClick={handleSaveReview} disabled={isSavingReview}>
                  {isSavingReview ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                  Save Review
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      {!isViewMode && canSaveAudit && isCreateMode ? (
        <div className="sticky bottom-0 left-0 right-0 border-t border-gray-200 bg-white/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-white/75">
          <div className="flex w-full items-center gap-4 px-4 py-4">
            <div className="text-sm">
              {error ? (
                <span className="text-red-600">{error}</span>
              ) : (
                <span className="text-gray-500">Save to keep this permit audit as a draft.</span>
              )}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/permit-audit', { state })} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Audit
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <Dialog open={isPermitDialogOpen} onOpenChange={setIsPermitDialogOpen}>
        <DialogContent className="flex max-h-[90vh] w-[min(96vw,1400px)] max-w-[96vw] flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0 px-6 pt-6">
            <DialogTitle>Select Permit</DialogTitle>
            <DialogDescription>Choose one or more permits that should be included in this audit.</DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-4">
            <div className="shrink-0 rounded-lg border bg-slate-50 px-4 py-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] flex-[1_1_320px]">
                  <Label htmlFor="permit-audit-search" className="mb-2 block text-sm font-semibold text-slate-900">
                    Permit No
                  </Label>
                  <Input
                    id="permit-audit-search"
                    value={permitSearch}
                    onChange={(event) => setPermitSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        setPermitSearchTrigger((value) => value + 1)
                      }
                    }}
                    placeholder="Search by permit number"
                  />
                </div>

                <div className="min-w-[160px] flex-[0_1_180px]">
                  <Label htmlFor="permit-audit-date-from" className="mb-2 block text-sm font-semibold text-slate-900">
                    Working Date
                  </Label>
                  <DatePicker
                    id="permit-audit-date-from"
                    value={permitDateFrom}
                    onChange={setPermitDateFrom}
                    placeholder="Select date..."
                    className="h-11"
                    allowPastDates
                  />
                </div>

                <div className="min-w-[160px] flex-[0_1_180px]">
                  <Label htmlFor="permit-audit-date-to" className="mb-2 block text-sm font-semibold text-slate-900">
                    To
                  </Label>
                  <DatePicker
                    id="permit-audit-date-to"
                    value={permitDateTo}
                    onChange={setPermitDateTo}
                    placeholder="Select date..."
                    className="h-11"
                    allowPastDates
                  />
                </div>

                <Button
                  type="button"
                  onClick={() => setPermitSearchTrigger((value) => value + 1)}
                  className="h-11 shrink-0 justify-center"
                >
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPermitSearch('')
                    setPermitDateFrom('')
                    setPermitDateTo('')
                    setPermitSearchTrigger((value) => value + 1)
                  }}
                  className="h-11 shrink-0 justify-center"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden rounded-lg border bg-white">
              <div className="h-full overflow-x-auto">
                <div className="max-h-full overflow-y-auto">
                  <table className="min-w-[1650px] caption-bottom text-sm">
                    <thead className="[&_tr]:border-b">
                      <tr className="border-b transition-colors hover:bg-muted/50">
                        <th className="h-12 min-w-[64px] px-4 text-left align-middle font-medium text-muted-foreground">Select</th>
                        <th className="h-12 min-w-[120px] px-4 text-left align-middle font-medium text-muted-foreground">Permit No</th>
                        <th className="h-12 min-w-[120px] px-4 text-left align-middle font-medium text-muted-foreground">Working Date</th>
                        <th className="h-12 min-w-[120px] px-4 text-left align-middle font-medium text-muted-foreground">Permit Time</th>
                        <th className="h-12 min-w-[260px] px-4 text-left align-middle font-medium text-muted-foreground">Company</th>
                        <th className="h-12 min-w-[160px] px-4 text-left align-middle font-medium text-muted-foreground">Plant</th>
                        <th className="h-12 min-w-[120px] px-4 text-left align-middle font-medium text-muted-foreground">Unit</th>
                        <th className="h-12 min-w-[180px] px-4 text-left align-middle font-medium text-muted-foreground">Permit Type</th>
                        <th className="h-12 min-w-[460px] px-4 text-left align-middle font-medium text-muted-foreground">Description</th>
                        <th className="h-12 min-w-[220px] px-4 text-left align-middle font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody className="[&_tr:last-child]:border-0">
                      {isPermitSearchLoading ? (
                        <tr className="border-b transition-colors hover:bg-muted/50">
                          <td colSpan={10} className="p-4 text-center align-middle text-sm text-gray-500">
                            Loading permits...
                          </td>
                        </tr>
                      ) : permitSearchResults.length === 0 ? (
                        <tr className="border-b transition-colors hover:bg-muted/50">
                          <td colSpan={10} className="p-4 text-center align-middle text-sm text-gray-500">
                            No permits found.
                          </td>
                        </tr>
                      ) : (
                        permitSearchResults.map((permit) => (
                          <tr
                            key={permit.permitNo}
                            className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                          >
                            <td className="p-4 align-middle">
                              <Checkbox
                                checked={Boolean(selectedPermitMap[permit.permitNo])}
                                onCheckedChange={(checked) =>
                                  setSelectedPermitMap((prev) => {
                                    if (checked === true) {
                                      return { ...prev, [permit.permitNo]: permit }
                                    }
                                    const next = { ...prev }
                                    delete next[permit.permitNo]
                                    return next
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-4 align-middle font-medium">{permit.permitNo}</td>
                            <td className="whitespace-nowrap p-4 align-middle">
                              {formatPermitDateRange(permit.plannedStart, permit.plannedEnd)}
                            </td>
                            <td className="whitespace-nowrap p-4 align-middle">
                              {formatPermitTimeRange(permit.plannedStart, permit.plannedEnd)}
                            </td>
                            <td className="p-4 align-middle">{permit.company ?? '-'}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{getPermitPlant(permit) ?? '-'}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{getPermitUnit(permit) ?? '-'}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{permit.permitType ?? '-'}</td>
                            <td className="p-4 align-middle">{permit.workDescr ?? permit.title ?? '-'}</td>
                            <td className="whitespace-nowrap p-4 align-middle">{permit.statusName ?? '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t px-6 py-4">
            <Button variant="outline" onClick={() => setIsPermitDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplySelectedPermits}>Apply Selection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
